import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReskinnedSidebar from '../components/sidebar/ReskinnedSidebar';
import { Flex } from '@chakra-ui/react';
import Navbar from '../components/navbar/navbar';
import '../styles/content-layout-fix.css';
import { setScreen } from '../redux/slices/setScreenSlice';
import { useDispatch } from 'react-redux';
import {
  useFeatureFlagFromContext,
  useFeatureFlagsFromContext,
} from '../launchdarkly';
import { redirectIfNoIntegrations } from '../utils/integrationCheck';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { createUrlToViewMap, createViewToUrlMap } from '../config/routes';
import { hasAuthMaterial } from '../utils/tokenService';
import { saveReturnPath, getCurrentPath } from '../utils/returnPath';
import TrialBanner from '../components/subscription/TrialBanner';
import {
  useSubscriptionStatus,
  MANAGE_SUBSCRIPTION_PATH,
} from '../components/subscription/useSubscriptionStatus';

const ReskinnedDashboard = () => {
  // Check if the reskin feature flag is enabled
  const showReskin = useFeatureFlagFromContext('showReskin', false);

  // Trial-expiring banner (PROF-1190): gated on the feature flag + self-serve cohort
  const {
    isEnabled: trialBannerEnabled,
    isSelfServe,
    isTrialing,
    isCancelled,
    trialEndDate,
    chargeDate,
    pricePerLocation,
    merchantKey,
  } = useSubscriptionStatus();
  // A cancelled trial keeps access until it ends but won't be charged, so the
  // "you'll be charged" banner is suppressed on every page in that case.
  const showTrialBanner =
    trialBannerEnabled &&
    isSelfServe &&
    isTrialing &&
    !isCancelled &&
    !!trialEndDate;

  // Get all feature flags to pass to child components
  const flags = useFeatureFlagsFromContext();

  // State hooks
  const [view, setView] = useState('dashboard');
  const [costDesc, setCostDesc] = useState(null);
  const [showSidebar, setShowSidebar] = useState(
    window.matchMedia('(min-width: 1000px)').matches
  );
  const [isMobile, setIsMobile] = useState(
    window.matchMedia('(max-width: 500px)').matches
  );
  const [visibleScreen, setVisibleScreen] = useState(null);

  // Fixed sidebar widths for ReskinnedSidebar (matches the component's fixed positioning)
  const getSidebarWidth = useCallback(() => {
    // ReskinnedSidebar starts collapsed (80px) and expands on hover to 280px
    // For layout purposes, we use the collapsed width as the base
    return '80px';
  }, []);

  const getContentWidth = useCallback(showSidebar => {
    if (showSidebar) {
      // Always account for the collapsed sidebar width since it's fixed positioned
      return 'calc(100% - 80px)';
    } else {
      return '100%';
    }
  }, []);

  // Initialize widths
  const [sidebarWidth, setSidebarWidth] = useState(getSidebarWidth());
  const [contentWidth, setContentWidth] = useState(
    getContentWidth(showSidebar)
  );

  // Function to toggle sidebar collapse (not used with ReskinnedSidebar but kept for compatibility)
  const toggleSidebar = useCallback(() => {
    // ReskinnedSidebar handles its own expand/collapse on hover
    // This function is kept for compatibility but doesn't need to do anything
  }, []);

  // Function to handle sidebar visibility
  const handleSidebar = useCallback(
    value => {
      setShowSidebar(value);
      setContentWidth(getContentWidth(value));
    },
    [getContentWidth]
  );

  // Redux dispatch
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Presence check for auth material (JWT access token or SSO cookies). This is a
  // synchronous "do we have any credentials at all" gate; validity of present-but-
  // stale material is still enforced asynchronously by tokenService.
  const authed = hasAuthMaterial();

  // Map URL paths to view names for reskinned experience (memoized for performance)
  const urlToViewMap = useMemo(() => createUrlToViewMap(), []);

  // Reverse mapping for navigation (memoized for performance)
  const viewToUrlMap = useMemo(() => createViewToUrlMap(), []);

  // Update view based on URL
  useEffect(() => {
    const mappedView = urlToViewMap[location.pathname];
    if (mappedView && mappedView !== view) {
      setView(mappedView);
    }
  }, [location.pathname, view, urlToViewMap]);

  // Handle window resize with useCallback to prevent recreation on each render
  const handleResize = useCallback(() => {
    const newShowSidebar = window.matchMedia('(min-width: 1000px)').matches;
    setShowSidebar(newShowSidebar);
    setIsMobile(window.matchMedia('(max-width: 500px)').matches);
    setSidebarWidth(getSidebarWidth());
    setContentWidth(getContentWidth(newShowSidebar));
  }, [getSidebarWidth, getContentWidth]);

  // Handle visible screen change
  const handleVisibleScreen = useCallback(screen => {
    setVisibleScreen(screen);
  }, []);

  // Set up event listeners
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Dispatch screen information to Redux
  useEffect(() => {
    dispatch(setScreen({ isMobile, showSidebar }));
  }, [dispatch, isMobile, showSidebar]);

  // Check for integrations redirect. Gated on auth material so an unauthenticated
  // visitor doesn't fire this request: its 401 is swallowed as "no integrations",
  // which would hard-redirect to /integrations and clobber the saved deep link.
  useEffect(() => {
    if (!authed) return;
    redirectIfNoIntegrations(showReskin);
  }, [showReskin, authed]);

  // Fail closed for unauthenticated visitors: if there is no auth material at all
  // (no JWT access token, no SSO cookies), remember where they were headed and
  // bounce them to a clean /login. Done in an effect (not the render body) so
  // render stays side-effect free; a plain navigate to "/login" (rather than
  // carrying the current query) avoids replaying a deep link's ?sso=true, which
  // login.jsx would treat as a completed SSO flow and loop on. Validity of
  // present-but-stale material is still enforced asynchronously by tokenService.
  useEffect(() => {
    if (authed) return;
    saveReturnPath(getCurrentPath());
    navigate('/login', { replace: true });
  }, [authed, navigate]);

  // Don't paint the authenticated shell while the redirect above is pending; this
  // avoids the brief "flash" of the layout on a deep link before login takes over.
  if (!authed) {
    return null;
  }

  return (
    <>
      {/* Fixed positioned ReskinnedSidebar and show only on desktop */}
      {!isMobile && (
        <ReskinnedSidebar
          onClick={value => {
            // Navigate to URL route for reskinned experience using memoized mapping
            const targetUrl = viewToUrlMap[value] || `/${value}`;
            navigate(targetUrl);
          }}
          view={view}
        />
      )}

      {/* Main content area with left margin to account for fixed sidebar */}
      <Flex
        direction={'column'}
        width={contentWidth}
        ml={showSidebar ? sidebarWidth : '0'} // Add left margin to account for fixed sidebar
      >
        <Flex width={'100%'}>
          <Navbar
            modeMobile={isMobile}
            clickThruScreen={costDesc}
            onClick={value => {
              // Navigate to URL route for reskinned experience using memoized mapping
              const targetUrl = viewToUrlMap[value] || `/${value}`;
              navigate(targetUrl);
            }}
            view={view}
            flags={flags}
          />
        </Flex>
        {showTrialBanner && (
          <Flex
            width={'100%'}
            sx={{
              // The Nexus Banner's outer wrapper is a plain block div and its
              // inner flex row (`.banner`) only spans its content width, so the
              // colored bar doesn't fill the column. Stretch both to 100%.
              '& > *': { flex: 1, width: '100%' },
              // The banner row defaults to `align-items: stretch`, and its icon
              // uses a taller line-height than the body text. On the action
              // variant (no title) the single message line then stretches inside
              // a taller box and sits high, reading bottom-heavy. Center the row
              // and the inner content so the icon and text share a baseline.
              '& [class*="banner___"]': {
                width: '100%',
                alignItems: 'center',
              },
              '& [class*="content___"]': { alignItems: 'center' },
              // The vendor always renders a title node (an empty <span> here).
              // Collapse it so it can't contribute a stray line box above the
              // message; :empty keeps the titled Subscription & Billing variant
              // untouched.
              '& [class*="content___"] > div > span:empty': { display: 'none' },
            }}
          >
            <TrialBanner
              trialEndDate={trialEndDate}
              chargeDate={chargeDate}
              pricePerLocation={pricePerLocation}
              merchantKey={merchantKey}
              onManageSubscription={() => navigate(MANAGE_SUBSCRIPTION_PATH)}
              // On the Subscription & Billing page the action would navigate to
              // the current route, so hide it and show the no-action variant.
              showAction={location.pathname !== MANAGE_SUBSCRIPTION_PATH}
            />
          </Flex>
        )}
        <Flex
          width={'100%'}
          p={0}
          className="widget-drawer-content max-width-container"
        >
          {/* Use Outlet for nested routing with context */}
          <Outlet
            context={{
              highlightDesc: costDesc,
              modeMobile: isMobile,
              clickThru: (screen, desc) => {
                // If screen is 'cost' and showReskin is true, navigate to 'reskinnedCost' instead
                const targetScreen =
                  screen === 'cost' && showReskin ? 'reskinnedCost' : screen;
                const targetUrl =
                  viewToUrlMap[targetScreen] || `/${targetScreen}`;
                navigate(targetUrl);
                setCostDesc(desc);
              },
              sidebarWidth,
              visibleScreen,
              flags,
            }}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default ReskinnedDashboard;
